
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { completeGameSession } from '@/services/progressService';
import { incrementAnonymousPlayedGamesCount } from '@/services/anonymousTracking';
import { recordAnalyticsEvent } from '@/services/analyticsService';
import GameCompletionDialog from '@/components/GameCompletionDialog';
import AuthDialog from '@/components/AuthDialog';
import { useToast } from '@/hooks/use-toast';

interface GameCompletionProps {
  sessionId: string | null;
  startTime: Date | null;
  onBackClick?: () => void; // Making this optional
}

// Max number of games a non-authenticated user can play
const MAX_FREE_GAMES = 2;

const GameCompletion: React.FC<GameCompletionProps> = ({ 
  sessionId, 
  startTime, 
  onBackClick 
}) => {
  const navigate = useNavigate();
  const session = useSession();
  const { toast } = useToast();
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [hasRecordedSession, setHasRecordedSession] = useState(false);
  const [gamesRemaining, setGamesRemaining] = useState(0);

  const handleCompletionDialogClose = () => {
    setShowCompletionDialog(false);
    // Use replace to avoid history stacking
    navigate('/', { replace: true });
  };

  const handleCreateAccount = () => {
    setShowCompletionDialog(false);
    setShowAuthDialog(true);
  };

  // Function to record game session completion
  const recordGameCompletion = async () => {
    if (hasRecordedSession) return;
    
    try {
      if (session && sessionId && startTime) {
        // Record the game session end for authenticated users
        const durationSeconds = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
        try {
          await completeGameSession(sessionId, durationSeconds);
          
          // Record analytics event for game completion
          recordAnalyticsEvent('game_complete', {
            session_id: sessionId,
            duration_seconds: durationSeconds
          });
          
          setHasRecordedSession(true);
          console.log("Game session recorded successfully");
        } catch (error) {
          console.error("Failed to record game session end:", error);
          
          // Only show toast error for authenticated users
          toast({
            title: "Error",
            description: "Could not update game session data",
            variant: "destructive",
          });
        }
      } else if (!session && startTime) {
        // For non-authenticated users, we'll still continue even if tracking fails
        try {
          const newCount = await incrementAnonymousPlayedGamesCount();
          
          // Record analytics event for anonymous game completion
          recordAnalyticsEvent('game_complete', {
            anonymous: true,
            played_count: newCount
          });
          
          setGamesRemaining(Math.max(0, MAX_FREE_GAMES - newCount));
          setHasRecordedSession(true);
        } catch (error) {
          // For anonymous users, just log the error but don't show a toast
          console.error("Failed to increment anonymous played games count:", error);
          // Still consider the session as recorded to prevent multiple attempts
          setHasRecordedSession(true);
          // Set a default for anonymous users if tracking fails
          setGamesRemaining(0);
        }
      }
    } catch (error) {
      // Mark as recorded even if there was an error to prevent retry loops
      setHasRecordedSession(true);
      console.error("Error in recordGameCompletion:", error);
      
      // Only show error toast to authenticated users
      if (session) {
        toast({
          title: "Error",
          description: "Could not update game session data",
          variant: "destructive",
        });
      }
    }
  };

  // Handler for back button click
  const handleBackClick = async () => {
    try {
      await recordGameCompletion();
    } catch (error) {
      console.error("Error recording game completion in back handler:", error);
    }
    
    if (!session) {
      // Show completion dialog for non-authenticated users before navigating away
      setShowCompletionDialog(true);
      return; // Prevent immediate navigation
    }
    
    // Execute custom back click handler if provided, otherwise navigate directly
    if (onBackClick) {
      onBackClick();
    } else {
      navigate('/', { replace: true });
    }
  };

  // Clean up function to record game session end on unmount
  useEffect(() => {
    return () => {
      recordGameCompletion().catch(error => {
        console.error("Failed to record game completion on unmount:", error);
      });
    };
  }, [session, sessionId, startTime]);

  return (
    <>
      {/* Game Completion Dialog */}
      <GameCompletionDialog 
        open={showCompletionDialog}
        onOpenChange={setShowCompletionDialog}
        gamesRemaining={gamesRemaining}
        onCreateAccount={handleCreateAccount}
        onClose={handleCompletionDialogClose}
      />

      {/* Auth Dialog */}
      <AuthDialog 
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
      />
    </>
  );
};

export default GameCompletion;
